/**
 * ZONIY - Checkout Success Page
 * @module CheckoutSuccessPage
 * @author Affan - Mobile Developer
 * @description Payment confirmation and order status
 * @version 1.0.0
 * Git: Affan <affan@linesoft.com> - Payment success handling
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { clearCart, fetchCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
        fetchCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="checkout-success-page">
        {status === 'checking' && (
          <>
            <Loader className="w-20 h-20 text-blue-600 mx-auto mb-6 animate-spin" />
            <h1 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase mb-4">
              Processing Payment
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="font-heading font-black text-4xl text-slate-900 dark:text-white uppercase mb-4" data-testid="success-title">
              Order Confirmed!
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-lg mb-8">
              Thank you for your purchase. Your order has been successfully placed.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 mb-8">
              <div className="flex items-center justify-center gap-2 text-slate-700 dark:text-slate-300">
                <Package className="w-5 h-5" />
                <span>Order confirmation sent to your email</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/orders" data-testid="view-orders-link">
                <Button className="btn-industrial">
                  View Orders <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/search">
                <Button variant="outline">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-orange-600" />
            </div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase mb-4">
              Session Expired
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/cart">
              <Button className="btn-industrial">
                Return to Cart
              </Button>
            </Link>
          </>
        )}

        {(status === 'timeout' || status === 'error') && (
          <>
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-slate-400" />
            </div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase mb-4">
              Status Unknown
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              We couldn't confirm your payment status. Please check your email for confirmation or view your orders.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/orders">
                <Button className="btn-industrial">
                  View Orders
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline">
                  Go Home
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default CheckoutSuccessPage;
